import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { useAuth } from '../../app/useAuth';
import { useT } from '../../i18n/i18n';
import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';
import { CheckEmail } from './CheckEmail';
import { useForm } from './useForm';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Sign-in and sign-up share one flow: enter email (and name), get a one-time link by email. */
export function EmailLinkForm({
  withName,
  title,
  subtitle,
  submitLabel,
  footer,
}: {
  withName: boolean;
  title: string;
  subtitle: string;
  submitLabel: string;
  footer: ReactNode;
}) {
  const t = useT();
  const { requestLink } = useAuth();
  const [params] = useSearchParams();
  const next = params.get('next') ?? undefined;
  const [sentTo, setSentTo] = useState<{ email: string; name?: string } | null>(null);

  const form = useForm(
    { name: '', email: '' },
    (v) => ({
      name: !withName || v.name.trim() ? undefined : t('auth.nameRequired'),
      email: EMAIL_RE.test(v.email.trim()) ? undefined : t('auth.emailInvalid'),
    }),
    async (v) => {
      const input = {
        email: v.email.trim().toLowerCase(),
        ...(withName && { name: v.name.trim() }),
      };
      await requestLink({ ...input, next });
      setSentTo(input);
    },
    () => undefined,
  );

  if (sentTo) {
    return (
      <AuthLayout title={t('auth.checkEmailTitle')}>
        <CheckEmail
          email={sentTo.email}
          resend={() => requestLink({ ...sentTo, next })}
          back={() => {
            form.reset();
            setSentTo(null);
          }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <form className={styles.form} onSubmit={form.onSubmit} noValidate>
        {form.errors._ && (
          <div className={styles.formError} role="alert">
            {form.errors._}
          </div>
        )}
        {withName && (
          <TextField
            label={t('auth.name')}
            placeholder={t('auth.namePlaceholder')}
            autoComplete="name"
            maxLength={60}
            value={form.values.name}
            onChange={form.set('name')}
            error={form.errors.name}
            disabled={form.submitting}
            autoFocus
          />
        )}
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          inputMode="email"
          hint={t('auth.emailHint')}
          value={form.values.email}
          onChange={form.set('email')}
          error={form.errors.email}
          disabled={form.submitting}
          autoFocus={!withName}
        />
        <Button type="submit" size="lg" block loading={form.submitting}>
          {submitLabel}
        </Button>
      </form>
      {footer}
    </AuthLayout>
  );
}
